import { useState } from "react";
import { useSearchParams, useParams, Link } from "react-router-dom";
import { Search, ArrowLeft, Calendar, Clock, Eye } from "lucide-react";
import { useSite, usePublic } from "../../lib/site";
import { href, fmtDate, stripHtml } from "../../lib/i18n";
import { lazyImgs } from "../../lib/html";
import { NewsCard, ShareButtons } from "../../components/site/Cards";
import { Seo, breadcrumbLd } from "../../components/site/Seo";
import { PageHeader } from "./PageView";
import { fmtCount, useViewCount } from "../../lib/text";
import NotFound, { Loading } from "./NotFound";

export function NewsList() {
  const { t, ui, lang } = useSite();
  const [params, setParams] = useSearchParams();
  const cat = params.get("kategori") || "";
  const page = Number(params.get("sayfa") || 1);
  const [q, setQ] = useState(params.get("q") || "");
  const { data: cats = [] } = usePublic(["news-cats"], "/public/news-categories");
  const qs = new URLSearchParams({ page, ...(cat && { category: cat }), ...(params.get("q") && { q: params.get("q") }) });
  const { data, isLoading } = usePublic(["news", qs.toString()], `/public/news?${qs}`);
  const update = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next);
  };
  const pill = (active) => `px-4 py-2 rounded-full text-sm font-medium border transition-colors ${active ? "bg-s-primary text-s-onprimary border-transparent" : "border-site-border hover:border-site-primary"}`;
  return (
    <>
      <Seo title={ui.news} jsonLd={data?.items?.length ? {
        "@context": "https://schema.org", "@type": "Blog", name: ui.latestNews,
        blogPost: data.items.slice(0, 10).map((n) => ({
          "@type": "BlogPosting", headline: t(n.title), url: window.location.origin + href(lang, "news", n.slug),
          datePublished: n.published_at, image: n.cover,
        })),
      } : null} />
      <PageHeader eyebrow={ui.news} title={ui.latestNews} />
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row gap-4 lg:items-center justify-between mb-12">
          <div className="flex flex-wrap gap-2" data-testid="news-category-filter">
            <button className={pill(!cat)} onClick={() => update({ kategori: "", sayfa: "" })} data-testid="news-cat-all">{ui.all}</button>
            {cats.map((c) => <button key={c.id} className={pill(cat === c.slug)} onClick={() => update({ kategori: c.slug, sayfa: "" })} data-testid={`news-cat-${c.slug}`}>{t(c.name)}</button>)}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); update({ q, sayfa: "" }); }} className="relative lg:w-80">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-s-muted" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={ui.search} data-testid="news-search-input"
              className="w-full rounded-full border border-site-border bg-transparent pl-11 pr-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[color:var(--s-primary)]" />
          </form>
        </div>
        {isLoading ? <Loading /> : data?.items?.length ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14" data-testid="news-grid">{data.items.map((n) => <NewsCard key={n.id} n={n} />)}</div>
        ) : <p className="text-s-muted py-20 text-center" data-testid="news-empty">{ui.noResults}</p>}
        {data?.pages > 1 && (
          <div className="flex justify-center gap-3 mt-16">
            <button disabled={page <= 1} onClick={() => update({ sayfa: page - 1 })} className={pill(false)} data-testid="news-prev-page">{ui.prev}</button>
            <span className="px-4 py-2 text-sm">{page} / {data.pages}</span>
            <button disabled={page >= data.pages} onClick={() => update({ sayfa: page + 1 })} className={pill(false)} data-testid="news-next-page">{ui.next}</button>
          </div>
        )}
      </div>
    </>
  );
}

export function NewsDetail() {
  const { slug } = useParams();
  const { lang, t, ui, site } = useSite();
  const { data: n, isLoading, isError } = usePublic(["news-item", slug], `/public/news/${slug}`);
  const views = useViewCount("news", n?.slug, n?.views);
  if (isLoading) return <Loading />;
  if (isError || !n) return <NotFound />;
  const title = t(n.title);
  const desc = t(n.seo?.description) || t(n.summary) || stripHtml(t(n.body)).slice(0, 155);
  const minutes = Math.max(1, Math.round(stripHtml(t(n.body)).split(" ").length / 200));
  const author = site.profile?.name || t(site.general?.site_name);
  const ld = { "@context": "https://schema.org", "@type": "NewsArticle", headline: title, datePublished: n.published_at, dateModified: n.updated_at, image: [n.cover], author: { "@type": "Person", name: site.profile?.name } };
  return (
    <article data-testid="news-detail">
      <Seo title={t(n.seo?.title) || title} description={desc} image={n.seo?.image || `/api/public/og/news/${n.slug}.png`} type="article" jsonLd={[ld, breadcrumbLd([{ name: ui.news, path: href(lang, "news") }, { name: title, path: href(lang, "news", n.slug) }])]} />
      <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-12 sm:pt-16">
        <Link to={href(lang, "news")} className="inline-flex items-center gap-2 text-sm text-s-muted hover:text-s-primary transition-colors" data-testid="news-back-link"><ArrowLeft size={15} /> {ui.news}</Link>
      </div>
      <header className="max-w-3xl mx-auto px-5 sm:px-8 pt-8 sm:pt-10 pb-12 rise">
        {n.category && <span className="inline-block px-3.5 py-1 rounded-full border border-site-border text-[11px] font-semibold uppercase tracking-[0.15em] text-s-primary mb-6" data-testid="news-detail-category">{t(n.category.name)}</span>}
        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1]" data-testid="page-title">{title}</h1>
        {t(n.summary) && <p className="mt-6 text-lg sm:text-xl text-s-muted leading-relaxed">{t(n.summary)}</p>}
        <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-s-muted border-t border-site-border pt-6">
          {author && <span className="font-semibold text-s-text">{author}</span>}
          <span className="flex items-center gap-1.5"><Calendar size={14} />{fmtDate(n.published_at, lang)}</span>
          <span className="flex items-center gap-1.5"><Clock size={14} />{minutes} {ui.minRead}</span>
          {views > 0 && <span className="flex items-center gap-1.5" data-testid="news-views"><Eye size={14} />{fmtCount(views, lang)} {lang === "tr" ? "okunma" : "reads"}</span>}
        </div>
        <div className="mt-6"><ShareButtons title={title} kind="news" imageUrl={n.cover} shareUrl={`${window.location.origin}/api/public/share/news/${n.slug}?v=${encodeURIComponent((n.updated_at || n.published_at || "").replace(/[^0-9]/g, "").slice(0, 12) || "1")}`} /></div>
      </header>
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <div className="prose-site" data-testid="news-body" dangerouslySetInnerHTML={{ __html: lazyImgs(t(n.body)) }} />
        {n.tags?.length > 0 && <div className="flex flex-wrap gap-2 mt-10">{n.tags.map((tg) => <span key={tg} className="text-xs px-3 py-1 rounded-full border border-site-border text-s-muted">#{tg}</span>)}</div>}
      </div>
      {n.related?.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-24">
          <h2 className="font-heading text-3xl font-bold mb-10">{ui.related}</h2>
          <div className="grid sm:grid-cols-3 gap-8">{n.related.map((r) => <NewsCard key={r.id} n={r} />)}</div>
        </section>
      )}
    </article>
  );
}
